import React, { memo, useCallback, useRef, useState } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isOverflowingX,
    useTruncationTooltip,
} from "@design-system/Tooltip/useTruncationTooltip";
import {
    MatchableItem,
    MatchItemType,
    AccrualMatchTableCallbacks,
    AccrualMatchSection,
} from "../types";
import tableStyles from "../AccrualMatchTable.module.scss";
import editAllocationStyles from "../EditAllocationDrawer.module.scss";

interface DescriptionCellOptions {
    section: AccrualMatchSection;
}

type DescriptionMeta = ViewTableMeta<
    AccrualMatchTableCallbacks,
    DescriptionCellOptions
>;

export const DescriptionCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const meta = table.options.meta as DescriptionMeta | undefined;
        const section = meta?.options?.section;
        const item = row.original;

        if (section === "remaining" && item.type === MatchItemType.REMAINING) {
            return (
                <ViewTableCell captureClicks>
                    <DescriptionInput
                        id={item.id}
                        value={item.description ?? ""}
                        onChange={meta?.callbacks?.onRemainingDescriptionChange}
                    />
                </ViewTableCell>
            );
        }

        // For transactions the column shows the raw bank description (the
        // display description is usually the counterparty name, which already
        // has its own column). Invoice/Bill rows have no bank description, so
        // they keep their document description.
        const text =
            item.type === MatchItemType.TRANSACTION
                ? (item.bankDescription ?? "")
                : (item.description ?? "");

        if (!text) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell className={tableStyles.descriptionCell}>
                <TruncatedDescription text={text} />
            </ViewTableCell>
        );
    },
);
DescriptionCell.displayName = "DescriptionCell";

/** One-line description that shows the full text in a tooltip when cut off. */
const TruncatedDescription = ({ text }: { readonly text: string }) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const truncationTooltipProps = useTruncationTooltip({
        ref: textRef,
        isTruncated: isOverflowingX,
    });

    return (
        <Tooltip content={text} {...truncationTooltipProps}>
            <span ref={textRef} className={tableStyles.descriptionText}>
                {text}
            </span>
        </Tooltip>
    );
};

const DescriptionInput = memo(
    ({
        id,
        value,
        onChange,
    }: {
        readonly id: string;
        readonly value: string;
        readonly onChange?: (id: string, description: string) => void;
    }) => {
        const [localValue, setLocalValue] = useState(value);
        const [prevValue, setPrevValue] = useState(value);
        if (value !== prevValue) {
            setPrevValue(value);
            setLocalValue(value);
        }

        const handleChange = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                setLocalValue(e.target.value);
            },
            [],
        );

        const handleBlur = useCallback(() => {
            onChange?.(id, localValue);
        }, [id, localValue, onChange]);

        return (
            <input
                type="text"
                className={editAllocationStyles.descriptionInput}
                value={localValue}
                onChange={handleChange}
                onBlur={handleBlur}
            />
        );
    },
);
DescriptionInput.displayName = "DescriptionInput";
